/* eslint-disable @typescript-eslint/no-unsafe-assignment */
"use client";

import type {
  AnnotationHandler,
  CodeAnnotation,
  HighlightedCode,
  InlineAnnotation,
} from "codehike/code";
import React, { useEffect, useMemo, useState } from "react";
import { highlight, InnerLine, Pre } from "codehike/code";

import { cn } from "@acme/ui";

import { useLighterTheme } from "./_utils/lighter-theme";
import { HighlighterErrorBoundary } from "./code-highlighter-error-boundary";

interface Callout {
  range: readonly [number, number, number, number];
  text: string;
}

const calloutHandler: AnnotationHandler = {
  name: "callout",
  transform: (annotation: InlineAnnotation) => {
    const { name, query, lineNumber, fromColumn, toColumn, data } = annotation;
    return {
      name,
      query,
      fromLineNumber: lineNumber,
      toLineNumber: lineNumber,
      data: { ...data, column: (fromColumn + toColumn) / 2 },
    };
  },
  Block: ({ annotation, children }) => {
    const { column } = annotation.data;
    return (
      <>
        {children}
        <div
          style={{ left: `${column}ch` }}
          className="bg-primary text-primary-foreground relative mt-1 -ml-[1ch] w-fit rounded border border-current px-2 whitespace-break-spaces"
        >
          <div className="bg-primary absolute -top-[1px] h-2 w-2 -translate-y-1/2 rotate-45 border-t border-l border-current" />
          {annotation.query}
        </div>
      </>
    );
  },
};

export const matchHandler: AnnotationHandler = {
  name: "match",
  Block: ({ children }) => <div className="bg-yellow-400/20">{children}</div>,
  Inline: ({ children }) => <span className="bg-primary/20">{children}</span>,
};

export function CodeHighlighter({
  code,
  language,
  callouts,
  className,
  lineNumberOffset,
}: {
  code: string;
  language: string;
  callouts?: Callout[];
  lineNumberOffset?: number;
  className?: string;
}) {
  const [highlightedCode, setHighlightedCode] =
    useState<HighlightedCode | null>(null);
  const highlighterTheme = useLighterTheme();

  const lineNumbersHandler: AnnotationHandler = useMemo(
    () => ({
      name: "line-numbers",
      Line: (props) => {
        const offset = lineNumberOffset ?? 0;
        const width = props.totalLines.toString().length + 1;
        return (
          <div className="flex">
            <span
              className="text-right opacity-50 select-none"
              style={{ minWidth: `${width}ch` }}
            >
              {props.lineNumber + offset}
            </span>
            <InnerLine merge={props} className="flex-1 pl-2" />
          </div>
        );
      },
    }),
    [lineNumberOffset],
  );

  useEffect(() => {
    if (!code) return;
    const realLanguage = language === "c_sharp" ? "csharp" : language;

    void (async () => {
      const annotations: CodeAnnotation[] = [];
      const codeLines = code.split("\n");
      const linesCount = codeLines.length;

      for (const callout of callouts ?? []) {
        const startLine = callout.range[0] + 1;
        const endLine = callout.range[2] + 1;
        const startColumn = callout.range[1] + 1;
        const endColumn = callout.range[3];

        annotations.push({
          name: "callout",
          query: callout.text,
          data: {},
          lineNumber: endLine,
          fromColumn: startColumn,
          toColumn: endColumn,
        });

        if (startLine === endLine) {
          annotations.push({
            name: "match",
            query: callout.text,
            data: {},
            lineNumber: Math.min(endLine, linesCount),
            fromColumn: startColumn,
            toColumn: endColumn,
          });
        } else {
          // Highlight the first line
          annotations.push({
            name: "match",
            query: callout.text,
            data: {},
            lineNumber: startLine,
            fromColumn: startColumn,
            toColumn: codeLines[startLine - 1]?.length ?? 1,
          });
          // Highlight the middle lines
          for (let i = startLine + 1; i < endLine; i++) {
            annotations.push({
              name: "match",
              query: callout.text,
              data: {},
              lineNumber: i,
              fromColumn: 1,
              toColumn: codeLines[i - 1]?.length ?? 1,
            });
          }
          // Highlight the last line
          annotations.push({
            name: "match",
            query: callout.text,
            data: {},
            lineNumber: endLine,
            fromColumn: 1,
            toColumn: endColumn,
          });
        }
      }

      try {
        const highlightedCode = await highlight(
          { lang: realLanguage, value: code, meta: "" },
          highlighterTheme,
        );

        setHighlightedCode({ ...highlightedCode, annotations });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [callouts, code, highlighterTheme, language]);

  if (!highlightedCode) {
    return null;
  }

  return (
    <HighlighterErrorBoundary>
      <Pre
        className={cn(
          "bg-background rounded-b-lg border px-4 py-2 text-xs whitespace-pre-wrap",
          className,
        )}
        code={highlightedCode}
        handlers={[calloutHandler, matchHandler, lineNumbersHandler]}
      />
    </HighlighterErrorBoundary>
  );
}
